import React, { useEffect, useRef, useState } from "react";
import { ArrowRight, CheckCircle2, ShieldCheck, Truck, Wrench } from "lucide-react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import Hero from "../components/Hero";
import Marquee from "../components/Marquee";
import Reveal from "../components/Reveal";
import ContactForm from "../components/ContactForm";
import { SERVICES, TANK_PARTS, STATS, PROCESS, TESTIMONIALS, BRAND } from "../mock";
import { Link } from "react-router-dom";

function Counter({ value }) {
  const ref = useRef(null);
  const [display, setDisplay] = useState("0");

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([e]) => {
        if (e.isIntersecting) {
          const m = value.match(/(\d+)/);
          if (!m) {
            setDisplay(value);
            obs.disconnect();
            return;
          }
          const target = parseInt(m[1], 10);
          const suffix = value.replace(m[1], "");
          let start = 0;
          const dur = 1400;
          const t0 = performance.now();
          const tick = (t) => {
            const p = Math.min(1, (t - t0) / dur);
            const eased = 1 - Math.pow(1 - p, 3);
            const cur = Math.floor(start + (target - start) * eased);
            setDisplay(cur + suffix);
            if (p < 1) requestAnimationFrame(tick);
          };
          requestAnimationFrame(tick);
          obs.disconnect();
        }
      },
      { threshold: 0.4 }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [value]);

  return (
    <span ref={ref} className="stat-value text-5xl md:text-6xl text-[#f5a623]">
      {display}
    </span>
  );
}

export default function Home() {
  return (
    <>
      <Header />
      <Hero />
      <Marquee />

      {/* Intro / Dealer band */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6 grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <Reveal>
            <span className="inline-block text-[11px] font-bold tracking-[0.25em] text-[#f5a623] mb-3">
              WHO WE ARE
            </span>
            <h2 className="display-font text-4xl md:text-5xl font-bold mb-5 leading-tight">
              Four decades of welds.<br/>
              <span className="text-[#f5a623]">One promise: it lasts.</span>
            </h2>
            <p className="text-[#444] text-lg leading-relaxed mb-6">
              EMC Industrial is a Miami-born manufacturing shop with a simple
              mission — build equipment that outworks the customer who bought it.
              We design, weld, and finish steel and aluminum work in-house, and
              we’re the proud authorized dealer for TATU Marchesan agricultural
              gear in the Southeast.
            </p>
            <ul className="space-y-3 mb-8">
              {[
                "In-house CNC plasma and machining",
                "AWS-certified welders, no contractors",
                "Same-day quotes • lifetime support",
              ].map((x) => (
                <li key={x} className="flex items-center gap-3 text-[#222]">
                  <CheckCircle2 className="w-5 h-5 text-[#f5a623] shrink-0" />
                  <span className="font-medium">{x}</span>
                </li>
              ))}
            </ul>
            <Link
              to="/about"
              className="inline-flex items-center gap-2 btn-primary px-6 py-3 font-semibold tracking-wide"
            >
              Our Story <ArrowRight className="w-4 h-4" />
            </Link>
          </Reveal>

          <Reveal delay={150} className="relative">
            <div className="relative overflow-hidden">
              <img
                src="https://images.unsplash.com/photo-1504328345606-18bbc8c9d7d1?w=900&q=85"
                alt="Welder fabricating steel"
                className="w-full h-[480px] object-cover"
              />
              <div className="absolute -bottom-1 -right-1 bg-[#f5a623] text-[#0a0a0a] p-6 max-w-[260px]">
                <div className="display-font text-3xl font-bold leading-none">EST. 1985</div>
                <div className="text-xs mt-2 font-semibold tracking-wider uppercase">Engineered. Manufactured. Delivered.</div>
              </div>
              <div className="absolute top-5 left-5 bg-[#0a0a0a] text-white px-3 py-1.5 text-[10px] font-bold tracking-[0.2em]">
                AUTHORIZED TATU DEALER
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Services grid */}
      <section id="services" className="py-20 bg-[#f7f7f7] bg-grid">
        <div className="max-w-7xl mx-auto px-6">
          <Reveal className="text-center max-w-2xl mx-auto mb-14">
            <span className="inline-block text-[11px] font-bold tracking-[0.25em] text-[#f5a623] mb-3">
              WHAT WE BUILD
            </span>
            <h2 className="display-font text-4xl md:text-5xl font-bold mb-4">
              Six core capabilities. One trusted shop.
            </h2>
            <p className="text-[#555] text-lg">
              From sub-assemblies to ready-to-haul rigs — every job leaves the
              floor pressure-tested and ready for the field.
            </p>
          </Reveal>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {SERVICES.map((s, i) => {
              const Icon = s.icon;
              return (
                <Reveal key={s.title} delay={i * 80}>
                  <div className="svc-card group bg-white border border-[#e6e6e6] p-7 h-full">
                    <div className="svc-icon-wrap inline-flex items-center justify-center w-14 h-14 bg-[#0a0a0a] text-[#f5a623] mb-5">
                      <Icon className="w-6 h-6" />
                    </div>
                    <h3 className="display-font text-xl font-bold mb-2.5">{s.title}</h3>
                    <p className="text-[#555] leading-relaxed mb-5">{s.desc}</p>
                    <Link to="/" className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#0a0a0a] group-hover:text-[#f5a623] transition-colors">
                      Learn more <ArrowRight className="w-4 h-4" />
                    </Link>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* Stats band */}
      <section className="py-16 bg-[#0a0a0a] relative overflow-hidden">
        <div
          className="absolute inset-0 opacity-15"
          style={{
            backgroundImage:
              "url('https://images.pexels.com/photos/13296053/pexels-photo-13296053.jpeg?w=1600')",
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        />
        <div className="relative max-w-7xl mx-auto px-6 grid grid-cols-2 md:grid-cols-4 gap-8">
          {STATS.map((s) => (
            <Reveal key={s.label} className="text-center">
              <Counter value={s.value} />
              <div className="mt-2 text-white/80 text-sm font-semibold uppercase tracking-wider">
                {s.label}
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* TATU Dealer feature */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6 grid grid-cols-1 lg:grid-cols-5 gap-10 items-center">
          <Reveal className="lg:col-span-2">
            <div className="bg-[#fff7e6] border-4 border-[#0a0a0a] p-10 flex flex-col items-center justify-center text-center">
              <div className="display-font text-5xl font-bold text-[#c8102e]">TATU</div>
              <div className="display-font text-2xl font-bold text-[#0033a0] mt-1">MARCHESAN</div>
              <div className="mt-3 px-3 py-1 bg-[#0a0a0a] text-[#f5a623] text-[10px] font-bold tracking-[0.2em]">
                AUTHORIZED DEALER
              </div>
              <p className="text-[#444] text-sm mt-5 leading-relaxed">
                Genuine parts, factory pricing, and the technical know-how to keep
                your equipment in the field, not the shop.
              </p>
            </div>
          </Reveal>

          <Reveal className="lg:col-span-3" delay={120}>
            <span className="inline-block text-[11px] font-bold tracking-[0.25em] text-[#f5a623] mb-3">
              OFFICIAL PARTNER
            </span>
            <h2 className="display-font text-4xl md:text-5xl font-bold mb-4 leading-tight">
              The TATU Marchesan name<br/>you trust — stocked in Miami.
            </h2>
            <p className="text-[#444] text-lg leading-relaxed mb-7">
              Whether you’re running a 60-acre citrus grove or a 6,000-acre sugar
              cane operation, we carry the harrows, parts, and tooling to keep
              your soil turning. Walk-in customers welcome — most parts ship
              same day.
            </p>
            <div className="flex flex-wrap gap-3">
              <Link to="/tatu-disk-harrows" className="btn-amber px-6 py-3 font-bold uppercase tracking-wider text-sm">
                View Disk Harrows
              </Link>
              <Link to="/tatu-replacement-parts" className="inline-flex items-center gap-2 border-2 border-[#0a0a0a] text-[#0a0a0a] px-6 py-3 font-bold uppercase tracking-wider text-sm hover:bg-[#0a0a0a] hover:text-white transition-colors">
                Replacement Parts <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Process */}
      <section className="py-20 bg-[#f7f7f7]">
        <div className="max-w-7xl mx-auto px-6">
          <Reveal className="text-center max-w-2xl mx-auto mb-14">
            <span className="inline-block text-[11px] font-bold tracking-[0.25em] text-[#f5a623] mb-3">
              HOW IT WORKS
            </span>
            <h2 className="display-font text-4xl md:text-5xl font-bold">From sketch to shop floor</h2>
          </Reveal>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {PROCESS.map((p, i) => (
              <Reveal key={p.step} delay={i * 100}>
                <div className="relative bg-white border border-[#e6e6e6] p-7 h-full hover:border-[#f5a623] transition-colors">
                  <div className="display-font text-6xl font-bold text-[#f5a623]/30 leading-none">{p.step}</div>
                  <h3 className="display-font text-xl font-bold mt-3 mb-2">{p.title}</h3>
                  <p className="text-[#555] leading-relaxed">{p.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Tank parts */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex flex-col md:flex-row md:items-end justify-between mb-10 gap-4">
            <Reveal>
              <span className="inline-block text-[11px] font-bold tracking-[0.25em] text-[#f5a623] mb-3">TANK PARTS</span>
              <h2 className="display-font text-4xl md:text-5xl font-bold">Components that keep tanks honest.</h2>
            </Reveal>
            <Reveal delay={150}>
              <Link to="/fuel-tanks" className="inline-flex items-center gap-2 text-sm font-bold uppercase tracking-wider hover:text-[#f5a623] transition-colors">
                See all parts <ArrowRight className="w-4 h-4" />
              </Link>
            </Reveal>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
            {TANK_PARTS.map((p, i) => {
              const Icon = p.icon;
              return (
                <Reveal key={p.title} delay={i * 80}>
                  <div className="svc-card bg-[#fafafa] border border-[#e6e6e6] p-6 h-full">
                    <div className="svc-icon-wrap inline-flex items-center justify-center w-12 h-12 bg-[#0a0a0a] text-[#f5a623] mb-4">
                      <Icon className="w-5 h-5" />
                    </div>
                    <h3 className="display-font text-lg font-bold mb-2">{p.title}</h3>
                    <p className="text-sm text-[#555] leading-relaxed">{p.desc}</p>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* Promise band */}
      <section className="py-16 bg-[#f5a623]">
        <div className="max-w-7xl mx-auto px-6 grid grid-cols-1 md:grid-cols-3 gap-6 text-[#0a0a0a]">
          {[
            { I: ShieldCheck, t: "2-Year Warranty", d: "Every fabricated build carries our standard parts & labor warranty." },
            { I: Truck, t: "Nationwide Shipping", d: "We crate, palletize, and ship to all 50 states from our Miami yard." },
            { I: Wrench, t: "Lifetime Support", d: "Need a tweak ten years from now? Same number. Same people. Real answers." },
          ].map(({ I, t, d }) => (
            <Reveal key={t} className="flex gap-4 items-start">
              <div className="w-14 h-14 bg-[#0a0a0a] text-[#f5a623] flex items-center justify-center shrink-0">
                <I className="w-6 h-6" />
              </div>
              <div>
                <h4 className="display-font text-xl font-bold mb-1">{t}</h4>
                <p className="text-[#0a0a0a]/80 leading-relaxed">{d}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20 bg-[#0a0a0a]">
        <div className="max-w-7xl mx-auto px-6">
          <Reveal className="text-center max-w-2xl mx-auto mb-12">
            <span className="inline-block text-[11px] font-bold tracking-[0.25em] text-[#f5a623] mb-3">
              FROM THE FIELD
            </span>
            <h2 className="display-font text-4xl md:text-5xl font-bold text-white">Trusted where it counts.</h2>
          </Reveal>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {TESTIMONIALS.map((t, i) => (
              <Reveal key={t.name} delay={i * 100}>
                <div className="bg-[#111] border border-[#1f1f1f] p-7 h-full hover:border-[#f5a623] transition-colors">
                  <div className="display-font text-4xl text-[#f5a623] leading-none mb-3">“</div>
                  <p className="text-white/85 leading-relaxed mb-6">{t.quote}</p>
                  <div className="pt-5 border-t border-[#1f1f1f]">
                    <div className="font-bold text-white">{t.name}</div>
                    <div className="text-xs text-[#f5a623] tracking-wider uppercase mt-0.5">{t.role}</div>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Contact */}
      <ContactForm />

      <Footer />
    </>
  );
}
